/**
 * ChatScope-Inspired Professional Conversation Sidebar
 * 
 * A modern conversation sidebar inspired by ChatScope.io design featuring:
 * - Dynamic profile avatars (initials, Gravatar, device icons)
 * - Rich customer context cards with device/location badges
 * - Professional conversation grouping and status indicators
 * - Smooth animations and micro-interactions
 * - Real-time status updates with live indicators
 * 
 * This component provides a professional chat interface that scales for
 * customer support teams while maintaining excellent UX.
 * 
 * @version 1.0.0
 * @author CleverAct AI Team
 * @inspired-by ChatScope.io
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Button } from '../../../ui/Button'
import { 
  User,
  MessageSquare,
  RefreshCw,
  AlertCircle,
  Smartphone,
  Monitor,
  Tablet,
  Globe,
  MapPin,
  Zap,
  UserCheck,
  Star,
  Dot
} from 'lucide-react'
import { StatusDot, getStatusConfig } from '../../../../utils/statusDisplay'
// import { useConversationList } from '../../../../hooks/useConversationHistory'
import { useRealTimeConversationList } from '../../../../hooks/useRealTimeNotifications'
import { useRealTimeConversations } from '../../../../hooks/useRealTimeConversations'
import { realTimeNotifications } from '../../../../services/realTimeNotifications'
import { realTimeConversationsService } from '../../../../services/realTimeConversations'
import { api } from '../../../../services/api'
import { logger } from '../../../../utils/logger'
import { useAuth } from '../../../../hooks/useAuth'
import { conversationHistoryService } from '../../../../services/conversationHistory'
import { liveChatService } from '../../../../services/liveChat'
import type { EnhancedConversationAssignment } from './ProfessionalConversationView'
import { PriorityBadge, type PriorityLevel } from '../PrioritySelector'

/**
 * Message interface for chat preview
 */
interface ConversationMessage {
  id: string
  role: 'user' | 'assistant' | 'agent' | 'system'
  content: string
  timestamp: string
  confidence_score?: number
  sources_used?: number
  model_used?: string
}

/**
 * ChatScope sidebar props
 */
export interface ChatScopeConversationSidebarProps {
  /** Currently selected conversation ID */
  selectedConversationId?: string
  /** Callback when conversation is selected */
  onConversationSelect: (conversation: EnhancedConversationAssignment) => void
  /** Real-time connection status */
  isRealTimeConnected?: boolean
  /** Real-time connection state */
  realTimeConnectionState?: string
  /** Last real-time update timestamp */
  lastRealTimeUpdate?: Date
  /** Current view mode */
  viewMode?: 'standard' | 'focus' | 'analytics'
  /** Callback when view mode changes */
  onViewModeChange?: (mode: 'standard' | 'focus' | 'analytics') => void
  /** Currently selected conversation for quick actions */
  selectedConversation?: EnhancedConversationAssignment | null
  /** Current user for quick actions */
  currentUser?: any
  /** Callback when quick action completes */
  onQuickActionComplete?: () => void
  /** Custom CSS classes */
  className?: string
}

/**
 * Profile avatar generation utilities
 */
const AvatarUtils = {
  /**
   * Generate initials from name
   */
  getInitials(name: string): string {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  },

  /**
   * Generate color from string (consistent for same user)
   */
  getAvatarColor(input: string): string {
    const colors = [
      'bg-blue-500',
      'bg-emerald-500', 
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-red-500',
      'bg-cyan-500',
      'bg-lime-500'
    ]
    
    const hash = input.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)
    
    return colors[Math.abs(hash) % colors.length]
  },

  /**
   * Get Gravatar URL
   */
  getGravatarUrl(email: string, size: number = 40): string {
    const hash = email.toLowerCase().trim()
    // In production, you'd use actual MD5 hash
    const simpleHash = btoa(email).slice(0, 8)
    return `https://www.gravatar.com/avatar/${simpleHash}?s=${size}&d=404`
  },

  /**
   * Get country flag emoji
   */
  getCountryFlag(countryCode: string): string {
    const flags: Record<string, string> = {
      'US': '🇺🇸', 'GB': '🇬🇧', 'CA': '🇨🇦', 'DE': '🇩🇪', 'FR': '🇫🇷',
      'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱', 'AU': '🇦🇺', 'JP': '🇯🇵',
      'KR': '🇰🇷', 'CN': '🇨🇳', 'IN': '🇮🇳', 'BR': '🇧🇷', 'MX': '🇲🇽'
    }
    return flags[countryCode.toUpperCase()] || '🌍'
  }
}

/**
 * ChatScope-style Profile Avatar Component
 */
const ProfileAvatar: React.FC<{
  conversation: EnhancedConversationAssignment
  size?: 'sm' | 'md' | 'lg'
  showOnlineStatus?: boolean
}> = ({ conversation, size = 'md', showOnlineStatus = true }) => {
  
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm', 
    lg: 'w-12 h-12 text-base'
  }
  
  const statusSize = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5'
  }

  // Profile data
  const visitorName = conversation.visitor_name || ''
  const visitorEmail = conversation.visitor_email || ''
  const deviceType = conversation.device_type?.toLowerCase() || 'unknown'
  const country = conversation.country || ''
  const isOnline = conversation.is_active || false

  // Avatar generation logic - memoized to prevent re-renders causing white flash
  const { avatarContent, avatarColor } = useMemo(() => {
    let content: React.ReactNode
    let color = 'bg-gray-500' // Default fallback color

    if (visitorName) {
      // Initials avatar (primary)
      const initials = AvatarUtils.getInitials(visitorName)
      color = AvatarUtils.getAvatarColor(visitorName)
      content = <span className="relative z-10">{initials}</span>
    } else if (visitorEmail) {
      // Email-based initials (secondary)
      const emailName = visitorEmail.split('@')[0]
      const initials = AvatarUtils.getInitials(emailName)
      color = AvatarUtils.getAvatarColor(visitorEmail)
      content = <span className="relative z-10">{initials}</span>
    } else {
      // Device icon fallback (tertiary)
      const deviceIcons = {
        mobile: Smartphone,
        desktop: Monitor,
        tablet: Tablet,
        unknown: User
      }
      const DeviceIcon = deviceIcons[deviceType as keyof typeof deviceIcons] || User
      color = 'bg-gray-500'
      content = <DeviceIcon className={`${size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-5 h-5' : 'w-6 h-6'} relative z-10`} />
    }

    return { avatarContent: content, avatarColor: color }
  }, [visitorName, visitorEmail, deviceType, size])

  // Build classes separately to ensure proper rendering
  const avatarClasses = `${sizeClasses[size]} ${avatarColor} rounded-full flex items-center justify-center font-semibold text-white relative flex-shrink-0`

  return (
    <div className="relative">
      <div className={avatarClasses}>
        {avatarContent}
      </div>
      
      {/* Online Status Indicator */}
      {showOnlineStatus && (
        <div className={`
          absolute -bottom-0.5 -right-0.5 ${statusSize[size]} 
          ${isOnline ? 'bg-emerald-500' : 'bg-gray-400'} 
          border-2 border-white rounded-full
          ${isOnline ? 'animate-pulse' : ''}
        `} />
      )}
      
      {/* Country Flag (for identified users) */}
      {country && (visitorName || visitorEmail) && (
        <div className="absolute -top-1 -right-1 text-xs">
          {AvatarUtils.getCountryFlag(country)}
        </div>
      )}
    </div>
  )
}

/**
 * Chat Message Preview Component
 */
const ChatMessagePreview: React.FC<{
  conversation: EnhancedConversationAssignment
}> = ({ conversation }) => {
  const [recentMessages, setRecentMessages] = useState<ConversationMessage[]>([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)

  // Fetch recent messages for preview
  useEffect(() => {
    const fetchRecentMessages = async () => {
      if (!conversation.conversation_id) return

      try {
        setIsLoadingMessages(true)
        
        // Fetch conversation history for message preview
        const response = await api.get(`/conversations/${conversation.conversation_id}/history`)
        
        // Handle nested response structure
        const conversationData = response.data.data || response.data
        if (conversationData && conversationData.messages) {
          // Get the last 3 messages and reverse for display order
          const allMessages = conversationData.messages
          const recentMsgs = allMessages.slice(-3).reverse()
          setRecentMessages(recentMsgs)
        }
      } catch (error) {
        logger.debug('Failed to fetch recent messages for preview', {
          conversationId: conversation.conversation_id,
          error: error instanceof Error ? error.message : 'Unknown error'
        })
        // Fallback to last_message if available
        if (conversation.last_message) {
          setRecentMessages([{
            id: 'fallback',
            role: 'user',
            content: conversation.last_message,
            timestamp: conversation.created_at
          }])
        }
      } finally {
        setIsLoadingMessages(false)
      }
    }

    fetchRecentMessages()
  }, [conversation.conversation_id, conversation.last_message])

  if (isLoadingMessages) {
    return (
      <div className="border-t border-gray-100 pt-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <RefreshCw className="w-3 h-3 animate-spin" />
          <span>Loading messages...</span>
        </div>
      </div>
    )
  }

  if (recentMessages.length === 0) {
    return null
  }

  return (
    <div className="border-t border-gray-100 pt-2 space-y-1.5">
      {recentMessages.slice(-2).map((message, index) => { // Show last 2 messages
        const isCustomer = message.role === 'user'
        const isAI = message.role === 'assistant'
        const isAgent = message.role === 'agent'
        
        const senderName = isCustomer 
          ? (conversation.visitor_name?.split(' ')[0] || 'Customer')
          : isAgent 
            ? 'Agent'
            : 'AI'

        const messageTime = formatTimeAgo(message.timestamp)

        return (
          <div key={message.id} className="flex items-start gap-1.5">
            {/* Sender Avatar/Icon */}
            <div className="flex-shrink-0 mt-0.5">
              {isCustomer ? (
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-2.5 h-2.5 text-white" />
                </div>
              ) : isAgent ? (
                <div className="w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                  <UserCheck className="w-2.5 h-2.5 text-white" />
                </div>
              ) : (
                <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                  <Zap className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0">
              {/* Sender and Time */}
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-xs font-medium text-gray-700">
                  {senderName}
                </span>
                <Dot className="w-2 h-2 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {messageTime}
                </span>
              </div>

              {/* Message Bubble */}
              <div className={`
                rounded-lg p-1.5 max-w-full text-xs leading-relaxed
                ${isCustomer 
                  ? 'bg-blue-50 border border-blue-100 rounded-bl-sm' 
                  : isAgent
                    ? 'bg-emerald-50 border border-emerald-100 rounded-br-sm'
                    : 'bg-purple-50 border border-purple-100 rounded-br-sm'
                }
              `}>
                <p className="text-gray-800 line-clamp-2 break-words">
                  {message.content}
                </p>
                
                {/* AI Confidence Indicator */}
                {isAI && message.confidence_score && (
                  <div className="flex items-center gap-1 mt-1 opacity-70">
                    <Star className="w-2 h-2 text-purple-600" />
                    <span className="text-xs text-purple-700">
                      {Math.round(message.confidence_score * 100)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
      
      {/* Show indicator if there are more messages */}
      {recentMessages.length > 2 && (
        <div className="text-center">
          <span className="text-xs text-gray-500">
            +{recentMessages.length - 2} more messages
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Device and Location Info Badge
 */
const DeviceLocationBadge: React.FC<{
  conversation: EnhancedConversationAssignment
}> = ({ conversation }) => {
  
  const deviceType = conversation.device_type?.toLowerCase() || 'unknown'
  const browserName = conversation.browser_name || ''
  const operatingSystem = conversation.operating_system || ''
  const city = conversation.city || ''
  const country = conversation.country || ''
  
  // Format device info
  const deviceInfo = []
  if (browserName) deviceInfo.push(browserName.split(' ')[0]) // Just browser name, not version
  if (deviceType !== 'unknown') deviceInfo.push(deviceType.charAt(0).toUpperCase() + deviceType.slice(1))
  if (country) deviceInfo.push(country)
  
  const deviceText = deviceInfo.length > 0 ? deviceInfo.join(' • ') : 'Unknown Device'
  
  // Get appropriate icon
  const deviceIcons = {
    mobile: Smartphone,
    desktop: Monitor,
    tablet: Tablet,
    unknown: Globe
  }
  const DeviceIcon = deviceIcons[deviceType as keyof typeof deviceIcons] || Globe
  
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500">
      <DeviceIcon className="w-3 h-3" />
      <span className="truncate">{deviceText}</span>
    </div>
  )
}

/**
 * WhatsApp/Telegram-Style Conversation Card
 */
const ChatScopeConversationCard: React.FC<{
  conversation: EnhancedConversationAssignment
  isSelected: boolean
  onSelect: () => void
  instantReadConversations?: Set<string>  // WhatsApp-style instant badge removal
  currentUser?: any  // For highlighting assigned conversations
}> = ({ conversation, isSelected, onSelect, instantReadConversations, currentUser }) => {
  
  // Conversation metadata
  const visitorName = conversation.visitor_name || conversation.customer_name || 'Anonymous User'
  const timeAgo = formatTimeAgo(conversation.last_message_time || conversation.created_at)
  const waitTime = conversation.wait_time || 0
  const messageCount = conversation.total_messages || 0
  const lastMessage = conversation.last_message || (messageCount > 0 ? 'Click to view messages' : 'No messages yet')
  const isUrgent = waitTime > 600 // 10+ minutes
  // Get real unread count from backend (WhatsApp-style notifications)
  const unreadCount = conversation.unread_count || 0
  
  // 🔍 DEBUG: Log unread count for debugging
  if (unreadCount > 0) {
    console.log(`🔵 CONVERSATION CARD: ${conversation.conversation_id} has unread count: ${unreadCount}`, {
      conversationId: conversation.conversation_id,
      unreadCount,
      visitorName: conversation.visitor_name,
      lastMessage: conversation.last_message
    })
  }
  
  // 🔍 DEBUG: Only log when there are actual issues or inconsistencies
  if (conversation.unread_count === undefined || (conversation.unread_count > 0 && !conversation.last_message)) {
    console.log(`🔍 CONVERSATION DATA DEBUG (Issue Detected): ${conversation.conversation_id}`, {
      conversationId: conversation.conversation_id,
      unreadCount: conversation.unread_count,
      hasUnreadCount: 'unread_count' in conversation,
      hasLastMessage: !!conversation.last_message,
      issue: conversation.unread_count === undefined ? 'Missing unread_count' : 'Unread messages but no last_message'
    })
  }
  
  // Get priority level (default to normal if not set)
  const priorityLevel = (conversation.priority || 'normal') as PriorityLevel
  
  // Check if conversation is assigned to the current user (for highlighting)
  const assignedAgentName = conversation.assigned_agent_name || conversation.current_agent_name
  const isAssignedToCurrentUser = currentUser && assignedAgentName && (
    assignedAgentName.toLowerCase().includes(currentUser.full_name?.toLowerCase()) ||
    assignedAgentName.toLowerCase().includes(currentUser.email?.toLowerCase()) ||
    conversation.assigned_agent_id === currentUser.id
  )
  
  // Use unified status display system for consistent status representation
  const statusConfig = getStatusConfig(conversation.status, assignedAgentName, conversation.assigned_agent_id)

  return (
    <div 
      className={`
        relative flex items-start gap-3 p-4 cursor-pointer transition-all duration-200 
        border-b border-gray-100 last:border-b-0
        ${isSelected ? 
          'bg-blue-100 border-l-4 border-l-blue-600 shadow-sm hover:bg-blue-100' : 
          'hover:bg-gray-50'
        }
        ${isUrgent && !isSelected ? 'bg-red-50/30' : ''}
        ${isAssignedToCurrentUser && !isSelected ? 'bg-green-50/40 border-l-2 border-l-green-400' : ''}
      `}
      onClick={onSelect}
    >
      
      {/* Profile Avatar with enhanced styling */}
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md ring-2 ring-white">
          {visitorName.slice(0, 2).toUpperCase()}
        </div>
        {/* Online/Active Status Indicator */}
        <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 border-2 border-white rounded-full shadow-sm ${
          conversation.is_active ? 'bg-green-400' : 'bg-gray-400'
        }`}></div>
      </div>
      
      {/* Main Content - WhatsApp Style Layout */}
      <div className="flex-1 min-w-0">
        
        {/* Header Row: Name, Time, Unread Count */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h4 className={`font-semibold text-sm truncate ${
              isSelected ? 'text-blue-900' : 
              unreadCount > 0 ? 'text-gray-900' : 'text-gray-800'
            }`}>
              {visitorName}
            </h4>
            {/* Priority indicator dots - Show for all priority levels */}
            {priorityLevel === 'urgent' && (
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>
            )}
            {priorityLevel === 'high' && (
              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0"></div>
            )}
            {priorityLevel === 'normal' && (
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></div>
            )}
            {priorityLevel === 'low' && (
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full flex-shrink-0"></div>
            )}
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Time */}
            <span className={`text-xs ${unreadCount > 0 ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
              {timeAgo}
            </span>
            
            {/* Unread Count Badge - Prominent WhatsApp Style */}
            {unreadCount > 0 && !instantReadConversations?.has(conversation.conversation_id) && (
              <div className="bg-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded-full min-w-[22px] h-[22px] flex items-center justify-center shadow-md">
                {unreadCount > 99 ? '99+' : unreadCount}
              </div>
            )}
          </div>
        </div>
        
        {/* Last Message Preview - Larger and more prominent */}
        <div className="mb-2">
          <p className={`text-sm leading-relaxed truncate ${
            unreadCount > 0 
              ? 'text-gray-900 font-medium' 
              : 'text-gray-600'
          }`}>
            {lastMessage}
          </p>
        </div>
        
        {/* Bottom Row: Status, Message Count, and Metadata */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* Compact Status Indicator - Using Unified Status System */}
            <StatusDot 
              status={conversation.status}
              assignedAgentName={assignedAgentName}
              assignedAgentId={conversation.assigned_agent_id}
              className="text-xs"
            />
            
            {/* Message Count */}
            {messageCount > 1 && (
              <span className="text-xs text-gray-500 flex-shrink-0">
                {messageCount} msg{messageCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          {/* Right side metadata */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Location if available */}
            {conversation.city && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500 truncate max-w-20">
                  {conversation.city}
                </span>
              </div>
            )}
            
            {/* Priority Badge - Show for all priority levels */}
            <PriorityBadge priority={priorityLevel} size="sm" showIcon={false} />
          </div>
        </div>
        
      </div>
      
    </div>
  )
}

/**
 * Format time for display
 */
const formatTimeAgo = (timestamp: string): string => {
  const now = new Date()
  const date = new Date(timestamp)
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
  
  if (diffInMinutes < 1) return 'now'
  if (diffInMinutes < 60) return `${diffInMinutes}m`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`
  return `${Math.floor(diffInMinutes / 1440)}d`
}

/**
 * Format wait time
 */
const formatWaitTime = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

/**
 * Main ChatScope-style Conversation Sidebar Component
 */
export const ChatScopeConversationSidebar: React.FC<ChatScopeConversationSidebarProps> = ({
  selectedConversationId,
  onConversationSelect,
  isRealTimeConnected = false,
  realTimeConnectionState = 'disconnected',
  lastRealTimeUpdate,
  viewMode = 'standard',
  selectedConversation,
  currentUser,
  onQuickActionComplete,
  className = ''
}) => {
  
  // Get current user for assignment highlighting
  const { user } = useAuth()
  
  // Component initialization
  useEffect(() => {
    logger.info('🎨 ChatScope-style ConversationSidebar initialized', {
      component: 'ChatScopeConversationSidebar',
      viewMode,
      realTimeConnected: isRealTimeConnected
    })
  }, [])

  // State management
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date())
  
  // 🎯 WhatsApp-style instant badge disappearance (optimistic UI)
  const [instantReadConversations, setInstantReadConversations] = useState<Set<string>>(new Set())

  // Real-time data with WebSocket - NO MORE POLLING!
  const { 
    conversations, 
    isLoading, 
    error, 
    totalUnread, 
    refresh, 
    isRealTime,
    setConversations
  } = useRealTimeConversationList()

  // 🎯 CRITICAL FIX: Also listen to conversation management WebSocket for widget_message_received events
  const {
    isConnected: isConversationManagementConnected
  } = useRealTimeConversations({
    enabled: true,
    subscribeToList: true,
    onRefreshNeeded: () => {
      logger.info('🔄 Conversation management WebSocket triggered refresh - WhatsApp-style direct update')
      // Don't do full refresh - let the real-time hooks handle it
      setLastRefreshTime(new Date())
    },
    onConversationUpdate: (conversationId: string, data: any) => {
      logger.info('💬 Conversation update from management WebSocket - WhatsApp-style direct update', {
        conversationId,
        data,
        timestamp: new Date().toISOString()
      })
      
      // 🎯 CRITICAL: Trigger a refresh for any conversation update to ensure sidebar consistency
      // The useRealTimeConversationList hook will then fetch the latest data
      refresh() // Call the refresh function from useRealTimeConversationList

      // Optionally, you can still handle specific data types if needed for optimistic UI updates
      if (data.type === 'new_message' && data.conversation_id && data.last_message) {
        logger.info('📨 Applying real-time message update to sidebar (optimistic)', {
          conversationId: data.conversation_id,
          lastMessage: data.last_message,
          unreadCount: data.unread_count
        })
        // setLastRefreshTime(new Date()) // This might not be needed if refresh() handles it
      }
    }
  })

  // Update on real-time changes
  useEffect(() => {
    if (lastRealTimeUpdate) {
      setLastRefreshTime(lastRealTimeUpdate)
      refresh()
    }
  }, [lastRealTimeUpdate, refresh])

  // Listen for real-time WebSocket conversation updates (including priority changes)
  useEffect(() => {
    const handleConversationUpdate = (event: CustomEvent) => {
      const { conversation_id, priority, old_priority, type } = event.detail || {}
      
      // Handle both conversation_update (WebSocket) and priorityUpdated (legacy) events
      if ((type === 'conversation_update' || event.type === 'priorityUpdated') && conversation_id && priority) {
        logger.info('📡 Real-time conversation update received in sidebar', {
          component: 'ChatScopeConversationSidebar',
          conversationId: conversation_id,
          oldPriority: old_priority,
          newPriority: priority,
          eventType: type || event.type
        })
        
        // Update timestamp to trigger UI refresh
        setLastRefreshTime(new Date())
        
        // ✨ No manual refresh needed! WebSocket real-time updates handle this automatically
        logger.info('✅ Sidebar UI will update automatically via real-time WebSocket data')
      }
    }
    
    // Listen for both WebSocket events and legacy priority update events
    window.addEventListener('conversation_update', handleConversationUpdate as EventListener)
    window.addEventListener('priorityUpdated', handleConversationUpdate as EventListener)
    
    return () => {
      window.removeEventListener('conversation_update', handleConversationUpdate as EventListener)
      window.removeEventListener('priorityUpdated', handleConversationUpdate as EventListener)
    }
  }, [])

  // Listen for new message notifications and refresh conversation list
  useEffect(() => {
    const handleMessageReceived = (event: CustomEvent) => {
      const { conversationId } = event.detail
      
      console.log('🔍 SIDEBAR: messageReceived event captured!', {
        component: 'ChatScopeConversationSidebar',
        conversationId,
        eventDetail: event.detail,
        timestamp: new Date().toISOString()
      })
      
      logger.info('📨 New message notification received in sidebar, refreshing conversation list', {
        component: 'ChatScopeConversationSidebar',
        conversationId,
        timestamp: new Date().toISOString()
      })
      
      // 🔄 FALLBACK: Log message received - real-time hooks handle the state update
      logger.info('📨 New message - real-time hooks will handle state update automatically')
    }
    
    console.log('🎧 SIDEBAR: Setting up messageReceived event listener')
    window.addEventListener('messageReceived', handleMessageReceived as EventListener)
    
    return () => {
      console.log('🎧 SIDEBAR: Removing messageReceived event listener')
      window.removeEventListener('messageReceived', handleMessageReceived as EventListener)
    }
  }, [])

  // Listen for conversation message updates (WhatsApp style - no refresh needed)
  useEffect(() => {
    const handleConversationMessageUpdate = (event: CustomEvent) => {
      const { conversationId, lastMessage, lastMessageTime, unreadCount, messageType } = event.detail
      
      logger.info('🔄 Conversation message update received in sidebar - real-time state update', {
        component: 'ChatScopeConversationSidebar',
        conversationId,
        lastMessage,
        lastMessageTime,
        unreadCount,
        messageType
      })
      
      // ✅ WhatsApp Style: The useConversationUpdates hook will automatically handle the state update
      // No need to force refresh - the conversation list state is updated directly via WebSocket events
      logger.info('📱 WhatsApp-style update: Conversation state updated via real-time hooks')
    }
    
    window.addEventListener('conversationMessageUpdate', handleConversationMessageUpdate as EventListener)
    
    return () => {
      window.removeEventListener('conversationMessageUpdate', handleConversationMessageUpdate as EventListener)
    }
  }, [])

  // Listen for unread count updates from real-time notifications
  useEffect(() => {
    const handleUnreadCountUpdate = (event: CustomEvent) => {
      const { conversationId, unreadCount } = event.detail
      
      console.log('🔵 SIDEBAR: unreadCountUpdate event captured!', {
        component: 'ChatScopeConversationSidebar',
        conversationId,
        unreadCount,
        eventDetail: event.detail,
        timestamp: new Date().toISOString()
      })
      
      // Real-time WebSocket updates handle unread count changes automatically
      // The useRealTimeConversationList hook will receive WebSocket updates
      setLastRefreshTime(new Date())
      logger.info('📊 Unread count updated - WebSocket handling real-time updates')
    }
    
    console.log('🎧 SIDEBAR: Setting up unreadCountUpdate event listener')
    window.addEventListener('unreadCountUpdate', handleUnreadCountUpdate as EventListener)
    
    return () => {
      console.log('🎧 SIDEBAR: Removing unreadCountUpdate event listener')
      window.removeEventListener('unreadCountUpdate', handleUnreadCountUpdate as EventListener)
    }
  }, [refresh])

  // Listen for new conversation assignments from chat widget
  useEffect(() => {
    const handleNewConversationAssigned = (event: CustomEvent) => {
      const { conversationId, conversationData } = event.detail
      
      logger.info('🆕 SIDEBAR: New conversation assigned, refreshing list', {
        conversationId,
        visitorName: conversationData?.visitor_name,
        handoffReason: conversationData?.handoff_reason,
        timestamp: new Date().toISOString()
      })
      
      // Force a refresh to show the new conversation immediately
      refresh()
    }
    
    logger.debug('🎧 SIDEBAR: Setting up newConversationAssigned event listener')
    window.addEventListener('newConversationAssigned', handleNewConversationAssigned as EventListener)
    
    return () => {
      logger.debug('🎧 SIDEBAR: Removing newConversationAssigned event listener')
      window.removeEventListener('newConversationAssigned', handleNewConversationAssigned as EventListener)
    }
  }, [refresh])

  // Listen for handoff_created events (customer requests agent)
  useEffect(() => {
    const handleHandoffCreated = (event: CustomEvent) => {
      const { conversation_id, status, handoff_status } = event.detail
      
      logger.info('🆕 SIDEBAR: Handoff created, refreshing list', {
        conversationId: conversation_id,
        status,
        handoff_status,
        timestamp: new Date().toISOString()
      })
      
      // Force a refresh to show the new conversation immediately
      refresh()
    }
    
    logger.debug('🎧 SIDEBAR: Setting up handoff_created event listener')
    window.addEventListener('handoff_created', handleHandoffCreated as EventListener)
    
    return () => {
      logger.debug('🎧 SIDEBAR: Removing handoff_created event listener')
      window.removeEventListener('handoff_created', handleHandoffCreated as EventListener)
    }
  }, [refresh])

  // Note: Conversation management WebSocket listening is now handled in useRealTimeConversationList hook

  // Listen for WebSocket conversation updates (assignments, transfers, resolutions)
  useEffect(() => {
    const handleConversationUpdate = (event: any) => {
      logger.debug('🔄 SIDEBAR: Conversation update detected via WebSocket, refreshing list', event)
      
      // Check if this is an assignment update (has assigned_agent_id)
      const { conversation_id, assigned_agent_id, status } = event?.detail || {}
      
      if (conversation_id) {
        logger.info('🔄 SIDEBAR: Conversation update detected, refreshing list', {
          conversationId: conversation_id,
          receivedStatus: status,
          timestamp: new Date().toISOString()
        })
        
        // Force a refresh to ensure we get the latest data with correct status mapping
        refresh()
      }
      
      // Real-time WebSocket updates handle conversation changes automatically
      // No manual refresh needed - WebSocket events update the UI in real-time
      setLastRefreshTime(new Date())
      logger.info('🔄 Conversation update via WebSocket - real-time UI update')
    }
    
    // Listen for conversation_update notifications from the real-time WebSocket
    logger.debug('🎧 SIDEBAR: Setting up conversation_update WebSocket listener')
    const unsubscribe = realTimeNotifications.addEventListener('conversation_update', handleConversationUpdate)
    
    // Also listen for DOM events for conversation updates
    window.addEventListener('conversation_update', handleConversationUpdate as EventListener)
    
    return () => {
      logger.debug('🎧 SIDEBAR: Removing conversation_update WebSocket listener')
      unsubscribe()
      window.removeEventListener('conversation_update', handleConversationUpdate as EventListener)
    }
  }, [refresh])

  // Deduplicate conversations by conversation_id to prevent duplicate records
  const deduplicatedConversations = useMemo(() => {
    if (!conversations) return []
    
    
    const conversationMap = new Map()
    conversations.forEach(conv => {
      const existingConv = conversationMap.get(conv.conversation_id)
      if (!existingConv || new Date(conv.created_at) > new Date(existingConv.created_at)) {
        // Keep the most recent record for each conversation_id
        conversationMap.set(conv.conversation_id, conv)
      }
    })
    
    return Array.from(conversationMap.values())
  }, [conversations])

  /**
   * Map raw status to enhanced status for filtering
   */
  const mapToEnhancedStatus = (rawStatus: string, isAssigned: boolean = false): string => {
    // Handle pending status - if assigned agent exists, it's unresolved, otherwise pending
    if (rawStatus === 'pending') {
      return isAssigned ? 'unresolved' : 'pending'
    }
    
    // 🔧 FIX: human_assigned should map to 'pending' to match the UI display
    // This status shows as "Pending" in the UI, so it should filter as pending
    if (rawStatus === 'human_assigned' && !isAssigned) {
      return 'pending';
    }
    
    // If status is assigned, human_assigned, unresolved, or escalated_human, and we have an agent, it's unresolved.
    if ((rawStatus === 'assigned' || rawStatus === 'human_assigned' || rawStatus === 'unresolved' || rawStatus === 'escalated_human') && isAssigned) {
      return 'unresolved';
    }
    
    // Keep ai_active as is to prevent it from being mapped to pending
    if (rawStatus === 'ai_active') {
      return 'ai_active'
    }
    
    // First check if the conversation is resolved/completed
    if (rawStatus === 'completed' || rawStatus === 'resolved') return 'resolved';
    
    // Return the status as-is for all other cases
    return rawStatus as ConversationStatus
  };

  /**
   * Filter conversations
   */
  const filteredConversations = useMemo(() => {
    const filtered = deduplicatedConversations.filter(conversation => {
      // Status filter - map the raw status to enhanced status before comparing
      // 🔧 FIX: Use same logic as display - check both agent_id and agent_name for consistency
      const isAssigned = !!conversation.assigned_agent_id || !!conversation.assigned_agent_name;
      const enhancedStatus = mapToEnhancedStatus(conversation.status, isAssigned);
      const matchesStatus = filterStatus === 'all' || enhancedStatus === filterStatus;
      
      // Debug logging for resolved conversations
      if (conversation.status === 'completed' || conversation.status === 'resolved') {
        logger.debug('🔍 Resolved conversation in filter', {
          conversationId: conversation.conversation_id,
          rawStatus: conversation.status,
          enhancedStatus,
          filterStatus,
          matchesStatus,
          willShow: matchesStatus
        })
      }
      
      return matchesStatus
    }).sort((a, b) => {
      // Sort by priority first (urgent > high > normal > low)
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 }
      const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 2
      const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 2
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority
      }
      
      // Then by unread count
      const aUnread = a.unread_count || 0
      const bUnread = b.unread_count || 0
      if (aUnread !== bUnread) {
        return bUnread - aUnread
      }
      
      // Finally by last message time
      const aTime = new Date(a.last_message_time || a.created_at).getTime()
      const bTime = new Date(b.last_message_time || b.created_at).getTime()
      return bTime - aTime
    })
    
    return filtered
  }, [deduplicatedConversations, filterStatus])

  // Handle conversation selection with instant badge removal
  const handleConversationClick = (conversation: EnhancedConversationAssignment) => {
    // 🚀 INSTANT: Remove badge immediately (WhatsApp-style)
    if (conversation.unread_count && conversation.unread_count > 0) {
      setInstantReadConversations(prev => new Set(prev).add(conversation.conversation_id))
      
      console.log('⚡ INSTANT: Badge removed for conversation', {
        conversationId: conversation.conversation_id,
        unreadCount: conversation.unread_count,
        timestamp: new Date().toISOString()
      })
    }
    
    // Call the original handler (this will trigger mark-as-read API)
    onConversationSelect(conversation)
  }

  /**
   * Handle manual refresh
   */
  const handleRefresh = useCallback(async () => {
    logger.info('🔄 Manual sync triggered (real-time data is continuously updated)', {
      component: 'ChatScopeConversationSidebar'
    })
    
    setLastRefreshTime(new Date())
    // Only sync on manual request - normally WebSocket provides real-time updates
    await refresh()
  }, [refresh])

  /**
   * Handle quick actions from sidebar
   */
  const handleQuickAction = useCallback(async (actionId: string) => {
    if (!selectedConversation) {
      alert('Please select a conversation first')
      return
    }
    
    logger.info('🚀 Quick action triggered from sidebar', {
      component: 'ChatScopeConversationSidebar',
      action: actionId,
      conversationId: selectedConversation.conversation_id,
      customerName: selectedConversation.visitor_name
    })
    
    try {
      switch (actionId) {
        case 'assign':
          // Use currentUser if passed, otherwise use the user from auth hook
          const userForAssignment = currentUser || user
          if (!userForAssignment) {
            alert('User authentication required')
            return
          }
          
          const userType = userForAssignment?.user_type || userForAssignment?.role || localStorage.getItem('user_type')
          const canAssign = userType === 'agent' || userType === 'company_owner' || userType === 'admin'
          
          if (canAssign) {
            const response = await api.get(`/live-chat/conversations/${selectedConversation.conversation_id}/available-agents?include_self=true`)
            const availableAgents = response.data.agents || []
            
            const userAgent = availableAgents.find(agent => 
              agent.email === userForAssignment.email || 
              agent.user_account_id === userForAssignment.id
            )
            
            if (!userAgent) {
              alert('No agent profile found. Please contact admin to set up your agent profile.')
              return
            }
            
            await conversationHistoryService.assignConversation(
              selectedConversation.conversation_id,
              userAgent.id,
              'Quick assignment from sidebar',
              'Agent took ownership via quick action'
            )
            
            logger.info('✅ Conversation assigned successfully via sidebar')
            
            // Force a refresh to ensure we get the latest data with correct status mapping
            await refresh()
            
            onQuickActionComplete?.()
          } else {
            alert('Only agents, company owners, and admins can assign conversations to themselves')
          }
          break
          
        case 'transfer':
          await liveChatService.transferToAi(selectedConversation.conversation_id)
          logger.info('✅ Conversation transferred back to AI from sidebar')
          onQuickActionComplete?.()
          break
          
        case 'resolve':
          await liveChatService.resolveConversation(
            selectedConversation.conversation_id,
            { message: 'Resolved via sidebar quick action' }
          )
          logger.info('✅ Conversation resolved from sidebar')
          onQuickActionComplete?.()
          break
          
        case 'escalate':
          alert('Escalation feature coming soon - select conversation and use the right panel for now')
          break
          
        default:
          logger.warn('❓ Unknown quick action:', actionId)
      }
    } catch (error) {
      logger.error('❌ Quick action failed from sidebar', {
        action: actionId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      alert(`Failed to ${actionId} conversation: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [selectedConversation, currentUser, user, onQuickActionComplete])

  // Calculate conversation stats using enhanced status mapping for consistency
  const stats = useMemo(() => {
    const total = conversations.length
    // 🔧 FIX: Use same logic as display and filter - check both agent_id and agent_name
    const pending = conversations.filter(conv => mapToEnhancedStatus(conv.status, !!conv.assigned_agent_id || !!conv.assigned_agent_name) === 'pending').length
    const unresolved = conversations.filter(conv => mapToEnhancedStatus(conv.status, !!conv.assigned_agent_id || !!conv.assigned_agent_name) === 'unresolved').length
    const resolved = conversations.filter(conv => mapToEnhancedStatus(conv.status, !!conv.assigned_agent_id || !!conv.assigned_agent_name) === 'resolved').length
    
    return { total, pending, unresolved, resolved }
  }, [conversations])

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">Conversations</h2>
            <p className="text-xs text-gray-600 mt-0.5">
              {stats.total} total • {stats.pending} pending • {stats.unresolved} unresolved • {stats.resolved} resolved
            </p>
          </div>
          
        </div>
        
        {/* Filter Buttons */}
        <div className="flex gap-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'unresolved', label: 'Unresolved' },
            { key: 'resolved', label: 'Resolved' }
          ].map((filter) => (
            <Button
              key={filter.key}
              variant={filterStatus === filter.key ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilterStatus(filter.key)}
              className={`h-7 px-3 text-xs ${
                filterStatus === filter.key 
                  ? 'bg-blue-600 text-white border-blue-600' 
                  : 'bg-transparent text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto bg-white">
        
        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Error loading conversations</span>
            </div>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
        )}
        
        {/* Loading State */}
        {isLoading && (!filteredConversations || filteredConversations.length === 0) && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">Loading conversations...</p>
            </div>
          </div>
        )}
        
        {/* Conversation List - WhatsApp Style */}
        <div className="divide-y divide-gray-100">
          {(filteredConversations || []).map((conversation, index) => {
            // Include visitor_name in key to force re-render when customer data loads
            const stableKey = `${conversation.conversation_id || conversation.id || `temp-${index}`}-${conversation.visitor_name || 'anon'}`
            return (
              <ChatScopeConversationCard
                key={stableKey}
                conversation={conversation}
                isSelected={selectedConversationId === conversation.conversation_id}
                onSelect={() => handleConversationClick(conversation)}
                instantReadConversations={instantReadConversations}
                currentUser={user}
              />
            )
          })}
        </div>
        
        {/* Empty State */}
        {!isLoading && (!filteredConversations || filteredConversations.length === 0) && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="font-medium text-gray-900 mb-1">No Conversations</h3>
              <p className="text-sm text-gray-600">
                {filterStatus !== 'all' 
                  ? 'No conversations match your current filters' 
                  : 'No conversations requiring attention right now'
                }
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-3 border-t border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Updated: {lastRefreshTime.toLocaleTimeString()}</span>
          {isRealTime && (
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              <span>Real-time</span>
            </div>
          )}
        </div>
      </div>
      
    </div>
  )
}

export default ChatScopeConversationSidebar